import {FormControl, FormGroup} from '@angular/forms';
import {
    HttpStatusCodesEnum,
    OrderRouteService, RouteGetOutputInterface, RouteGetOutputModel, RouteUpdateInputModel,
    ServerResponse, ServerResponseData
} from '@spa/api';
import {
    RouteFormControls, RouteFormControlsInterface
} from 'appAssets/descriptors';
import {Helper} from 'appShared/classes';
import {map} from 'rxjs/operators';
import {OrderRoutesFormErrorStatesEnum} from '../../enums';

export class RouteForm {
    private orderRouteService: OrderRouteService;
    public formGroup: FormGroup;
    public controls: RouteFormControlsInterface;
    public routeId?: number;
    public routeUpdateInputModel: RouteUpdateInputModel;

    constructor(orderRouteService: OrderRouteService, routeId?: number, routeUpdateInputModel?: RouteUpdateInputModel) {
        this.routeId = routeId;
        this.orderRouteService = orderRouteService;
        this.routeUpdateInputModel = routeUpdateInputModel;
    }

    initForm(): void {
        this.formGroup = new FormGroup({});
        this.controls = RouteFormControls();
        Object.keys(this.controls).forEach((formItemKey: string) => {
            const control = this.controls[formItemKey];
            switch (control.name) {
                case RouteUpdateInputModel.getExecutionBrokerIdSelector():
                    control.value = !this.routeUpdateInputModel ? null : this.routeUpdateInputModel.getExecutionBrokerId();
                    break;
                case RouteUpdateInputModel.getDisplayNameSelector():
                    control.value = !this.routeUpdateInputModel ? null : this.routeUpdateInputModel.getDisplayName();
                    break;
                case RouteUpdateInputModel.getSubDestinationSelector():
                    control.value = !this.routeUpdateInputModel ? null : this.routeUpdateInputModel.getSubDestination();
                    break;
            }

            this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

            return control;
        });
    }

    updateRoute(): ServerResponse<RouteGetOutputModel> {
        const clonedModel = this.routeUpdateInputModel.serialize();

        this.routeUpdateInputModel.setExecutionBrokerId(this.formGroup.value[this.controls.broker.name]);
        this.routeUpdateInputModel.setDisplayName(this.formGroup.value[this.controls.displayName.name]);
        this.routeUpdateInputModel.setSubDestination(this.formGroup.value[this.controls.subDestination.name]);

        return this.orderRouteService.updateRoute(this.routeId, this.routeUpdateInputModel.serialize())
            .pipe(
                map((response: ServerResponseData<RouteGetOutputInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        this.routeUpdateInputModel = new RouteUpdateInputModel
                        (clonedModel.execution_broker_id, clonedModel.display_name, clonedModel.sub_destination);

                        return Helper.errorsHelper().createErrorResponse<RouteGetOutputInterface>(
                            response,
                            OrderRoutesFormErrorStatesEnum.UpdateRoutesFailed
                        );
                    }

                    return Helper.createSuccessResponse<RouteGetOutputModel>(new RouteGetOutputModel({
                        id: this.routeId,
                        execution_broker_id: this.routeUpdateInputModel.getExecutionBrokerId(),
                        display_name: this.routeUpdateInputModel.getDisplayName(),
                        sub_destination: this.routeUpdateInputModel.getSubDestination(),
                        created_by_id: null,
                        created_at: null,
                    }));
                })
            );
    }

    registerRoute(): ServerResponse<RouteGetOutputModel> {
        const inputModel = new RouteUpdateInputModel(
            this.formGroup.value[this.controls.broker.name],
            this.formGroup.value[this.controls.displayName.name],
            this.formGroup.value[this.controls.subDestination.name]
        );

        return this.orderRouteService.createRoute(inputModel.serialize()).pipe(
            map((response: ServerResponseData<RouteGetOutputInterface>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse<RouteGetOutputInterface>(
                        response,
                        OrderRoutesFormErrorStatesEnum.CreateRoutesFailed
                    );
                }
                const result = response.data as RouteGetOutputInterface;

                return Helper.createSuccessResponse<RouteGetOutputModel>(new RouteGetOutputModel(result));
            })
        );
    }


    createRequest(): ServerResponse<RouteGetOutputModel> {
        if (!this.routeUpdateInputModel) {
            return this.registerRoute();
        }

        return this.updateRoute();
    }

    validateForm(messagesFunction: (controlsLabel?: string) => { [key: string]: string }): boolean {
        let isInvalidForm = false;
        if (this.formGroup.invalid) {
            Object.keys(this.controls).forEach((controlKey: string) => {
                const controlName = this.controls[controlKey].name;
                const formControl = this.formGroup.get(controlName);

                if (formControl && formControl.invalid) {
                    const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messagesFunction(this.controls[controlKey].placeholder)[errorKey];
                    });

                    this.controls[controlKey].setErrors(errors);
                }

            });
            isInvalidForm = true;
        }

        return !isInvalidForm;
    }
}
